import { FC } from "react";

import clsx from "clsx";
import DemoFormSection from "components/demo-form-section";
import Heading from "components/heading";
import HeroSection, { HeroSectionContainer } from "components/hero-section";
import RichPortableText from "components/rich-text";
import ImageRenderer from "components/rich-text/components/image";
import { SanityRelatedItems, SanitySideNavSection } from "components/rich-text/types";
import SideNav from "components/side-nav";
import { backgroundMapping, textContrasts } from "components/views/resources/constants";
import { ColorOptions, ResourceItem } from "components/views/resources/types";
import { format, parse } from "date-fns";
import { filterDataToSingleItem } from "lib/sanity/helpers";
import { excludeDraftFilter, getResourceBySlugQuery, imageFragment } from "lib/sanity/queries";
import { getClient } from "lib/sanity/server";
import { useNextSanityImage } from "next-sanity-image";
import Link from "next/link";

import { ChevronLeftIcon } from "@heroicons/react/outline";

import Page from "../../components/layout/page";

type ResourcePageProps = { resource: ResourceItem; preview?: boolean };
const ResourcePage: FC<ResourcePageProps> = ({ resource, preview }: ResourcePageProps) => {
  const { seoTitle, seoDescription, title, content, authors, image, sideNav, navbar, footer, postedDate } =
    resource || {};
  const featuredImage = useNextSanityImage(getClient(), image || null);

  if (!resource) {
    return null;
  }

  const authorsText = authors?.map(({ firstName, lastName }) => `${firstName} ${lastName}`).join(", ");

  return (
    <Page
      title={seoTitle}
      description={seoDescription}
      imageUrl={featuredImage?.src}
      navItems={navbar?.variant === "default" ? undefined : navbar?.items || []}
      footer={footer}
    >
      <div className={clsx(backgroundMapping[ColorOptions.Gray], textContrasts[ColorOptions.Gray])}>
        <HeroSectionContainer className="items-center px-4">
          <div>
            <Link href="/resources" passHref>
              <a className="text-gray-500 hover:text-gray-600 inline-flex items-center gap-1 mt-4 md:mt-6">
                <ChevronLeftIcon className="h-5 w-5 relative -top-px" />
                Back to Resources
              </a>
            </Link>
          </div>
        </HeroSectionContainer>
        <HeroSection className="pb-10 sm:pb-14">
          <div className="flex-grow px-6 sm:px-10 lg:px-14 text-center">
            <div className="pb-6 md:pb-10 lg:pb-14">
              <div className="flex items-center justify-center my-4 md:mt-0">
                <Heading tag="h2">{title}</Heading>
              </div>
              <p className="text-gray-500 text-sm md:text-base">
                By {authorsText} | {format(parse(postedDate, "yyyy-MM-dd", new Date()), "MMMM dd, yyyy")}
              </p>
            </div>
            {image && <ImageRenderer value={image} imageClassName="rounded-lg w-full" layout="responsive" />}
          </div>
        </HeroSection>
        <HeroSectionContainer className={clsx(sideNav?.show && sideNav?.side === "left" && "flex-row-reverse")}>
          <div className="grow">
            {content?.map(section => (
              <div
                key={section._key}
                className={clsx(
                  backgroundMapping[section.backgroundColor || ColorOptions.Gray],
                  (section.backgroundColor && textContrasts[section.backgroundColor]) || "text-black"
                )}
              >
                <HeroSection>
                  <div className="flex-grow px-6 space-y-6 sm:px-10 lg:px-14 text-left">
                    <RichPortableText value={section.content} />
                  </div>
                </HeroSection>
              </div>
            ))}
          </div>
          {sideNav?.show && (
            <SideNav
              sections={sideNav.sections.map(section => ({
                ...section,
                content: section.content.map(content => ({ ...content, isPreview: preview, baseUrl: "/resources" })),
              }))}
              side={sideNav.side}
            />
          )}
        </HeroSectionContainer>
      </div>

      <DemoFormSection />
    </Page>
  );
};

export default ResourcePage;

export async function getStaticPaths() {
  const paths = await getClient().fetch(
    `*[_type == "resource" && ${excludeDraftFilter} && defined(slug.current)][].slug.current`
  );

  return {
    paths: paths.map(slug => ({ params: { slug } })),
    fallback: true,
  };
}

const getRelatedItemsContentItem = (section: SanitySideNavSection) =>
  section.content.findIndex(c => c._type === "relatedItems");

export async function getStaticProps(ctx) {
  const {
    params: { slug = "" },
    preview = null,
  } = ctx;

  const client = getClient(preview);
  let resource: ResourceItem | null = null;

  try {
    const result = await client.fetch(getResourceBySlugQuery(preview), { slug });
    resource = filterDataToSingleItem(result, preview);
  } catch (e) {
    console.error("Unable to fetch resource", e);
  }

  if (!resource) {
    return { notFound: true };
  }

  if (resource?.sideNav) {
    const relatedItemsSections = resource?.sideNav.sections.filter(s => getRelatedItemsContentItem(s) !== -1);

    // Fetch related items for each section requesting them
    if (relatedItemsSections?.length) {
      await Promise.all(
        relatedItemsSections.map(async section => {
          const sectionIndex = resource!.sideNav!.sections.findIndex(s => s._key === section._key);
          const contentIndex = getRelatedItemsContentItem(section);
          const contentItem = section.content[contentIndex] as SanityRelatedItems;

          try {
            const result = await client.fetch(
              `
              *[_type == "${contentItem!.type}" && _id != "${
                resource!._id
              }" && ${excludeDraftFilter}] | order(postedDate desc) [0...${contentItem.itemCount}] {
                _id,
                image {
                  ${imageFragment}
                },
                postedDate,
                title,
                "slug":slug.current,
                type->
              }`
            );
            (resource!.sideNav!.sections[sectionIndex].content[contentIndex] as SanityRelatedItems).items = result;
          } catch (e) {
            console.error("Unable to fetch related items", e);
          }
        })
      );
    }
  }

  return {
    props: {
      preview,
      resource,
    },
  };
}
